import { keysToSnake } from '#/api/adapters/wireCase';

function emptyToNull(value) {
  if (value == null) {
    return null;
  }
  const trimmed = String(value).trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * Write line：serialNo／problemDescription／選填 accessories／日期／remark／version。
 * 禁送 id、productCode、itemName（read-only；PATCH 整表取代亦不回送舊 id）。
 */
function mapLineToWritePayload(line) {
  if (!line || typeof line !== 'object') {
    return null;
  }
  return {
    serialNo: emptyToNull(line.serialNo),
    problemDescription: emptyToNull(line.problemDescription),
    accessories: emptyToNull(line.accessories),
    salesDate: emptyToNull(line.salesDate),
    warrantyExpiryDate: emptyToNull(line.warrantyExpiryDate),
    remark: emptyToNull(line.remark),
    version: emptyToNull(line.version),
  };
}

const HEADER_SNAPSHOT_KEYS = [
  'customerName',
  'contactName',
  'phone',
  'fax',
  'taxId',
  'address',
  'receivedDate',
];

/**
 * Create body（camel）。
 * 必填：repairRequestNo、repairRequestDate、非空 lines。
 * 選填 customerId（只收 ACTIVE 客戶）；空值不送。
 * 禁送 status／record／history／reservationId／line id／productCode／itemName。
 */
export function toRepairRequestCreateBody(values = {}) {
  const customerId = emptyToNull(values.customerId);
  return {
    ...(customerId ? { customerId } : {}),
    repairRequestNo: emptyToNull(values.repairRequestNo),
    repairRequestDate: emptyToNull(values.repairRequestDate),
    remark: emptyToNull(values.remark),
    customerName: emptyToNull(values.customerName),
    contactName: emptyToNull(values.contactName),
    phone: emptyToNull(values.phone),
    fax: emptyToNull(values.fax),
    taxId: emptyToNull(values.taxId),
    address: emptyToNull(values.address),
    receivedDate: emptyToNull(values.receivedDate),
    lines: (values.lines ?? []).map(mapLineToWritePayload).filter(Boolean),
  };
}

/**
 * PATCH body（camel）。含 `updatedAt`（＝ record.updatedAt）。
 * 若出現 `lines`＝整表取代；只改 header 時省略 `lines`。
 * `customerId`：省略＝保留既有關聯；明示 null＝清除。
 * 禁送 repairRequestNo／status／record／history／reservationId。
 */
export function toRepairRequestUpdateBody(values = {}) {
  const updatedAt = values.updatedAt ?? values.record?.updatedAt ?? null;
  const payload = {};

  if (Object.prototype.hasOwnProperty.call(values, 'repairRequestDate')) {
    payload.repairRequestDate = emptyToNull(values.repairRequestDate);
  }
  if (Object.prototype.hasOwnProperty.call(values, 'remark')) {
    payload.remark = emptyToNull(values.remark);
  }
  for (const key of HEADER_SNAPSHOT_KEYS) {
    if (Object.prototype.hasOwnProperty.call(values, key)) {
      payload[key] = emptyToNull(values[key]);
    }
  }
  if (Object.prototype.hasOwnProperty.call(values, 'customerId')) {
    payload.customerId = emptyToNull(values.customerId);
  }
  if (Object.prototype.hasOwnProperty.call(values, 'lines')) {
    payload.lines = (values.lines ?? []).map(mapLineToWritePayload).filter(Boolean);
  }
  if (updatedAt != null && updatedAt !== '') {
    payload.updatedAt = updatedAt;
  }

  return payload;
}

/**
 * delete／establish／complete：body 僅 `{ updatedAt }`。
 */
export function toRepairRequestCommandBody(payload = {}) {
  const updatedAt = payload.updatedAt ?? payload.record?.updatedAt ?? null;
  const body = {};
  if (updatedAt != null && updatedAt !== '') {
    body.updatedAt = updatedAt;
  }
  return body;
}

/** POST `/aftersales/repair-request` */
export function serializeRepairRequestCreate(values) {
  return keysToSnake(toRepairRequestCreateBody(values));
}

/** PATCH `/aftersales/repair-request/{id}` */
export function serializeRepairRequestUpdate(values) {
  return keysToSnake(toRepairRequestUpdateBody(values));
}

/** POST `…/{id}/delete|establish|complete` */
export function serializeRepairRequestCommand(payload) {
  return keysToSnake(toRepairRequestCommandBody(payload));
}
